import { helpers } from "@vuelidate/validators";

/** Allows digits, spaces, and the characters commonly used in international phone numbers: + - ( ) . */
export const validPhoneNumber = helpers.withMessage(
    "Must be a valid phone number (digits, spaces, and + - ( ) . only)",
    (value: string) => !helpers.req(value) || /^[\d\s+\-().]+$/.test(value),
);

export const isEmptyOrUndefinedString = (variable: any) => {
    return ["undefined", undefined, "null", null, ""].includes(variable);
};

export const getValidationMessages = (model) => {
    /**
     * Returns a list of validation messages for each input if the validation
     * is invalid.
     */
    const errors: string[] = [];
    if (!model.$dirty) return errors;
    else {
        if (model.$error) {
            if (model.required && model.required.$invalid) {
                errors.push("This value is required");
            }
            if (model.fileSizeLimit && model.fileSizeLimit.$invalid) {
                errors.push(model.fileSizeLimit.$message);
            }
            if (model.validBirthdate && model.validBirthdate.$invalid) {
                errors.push(model.validBirthdate.$message);
            }
            if (model.minValue && model.minValue.$invalid) {
                errors.push(model.minValue.$message);
            }
            if (model.maxValue && model.maxValue.$invalid) {
                errors.push(model.maxValue.$message);
            }
            if (model.url && model.url.$invalid) {
                errors.push(model.url.$message);
            }
            if (model.maxLength && model.maxLength.$invalid) {
                errors.push(model.maxLength.$message);
            }
            if (model.minLength && model.minLength.$invalid) {
                errors.push(model.minLength.$message);
            }
            if (model.numeric && model.numeric.$invalid) {
                errors.push(model.numeric.$message);
            }
            if (model.alphaNum && model.alphaNum.$invalid) {
                errors.push(model.alphaNum.$message);
            }
            if (model.email && model.email.$invalid) {
                errors.push(model.email.$message);
            }
            if (model.validShortcode && model.validShortcode.$invalid) {
                errors.push(model.validShortcode.$message);
            }
            if (model.validBacsReference && model.validBacsReference.$invalid) {
                errors.push(model.validBacsReference.$message);
            }
            if (model.positiveNumberRule && model.positiveNumberRule.$invalid) {
                errors.push(model.positiveNumberRule.$message);
            }
            if (model.requiredIf && model.requiredIf.$invalid) {
                errors.push("This value is required");
            }
            if (model.validPhoneNumber && model.validPhoneNumber.$invalid) {
                errors.push(model.validPhoneNumber.$message);
            }
            if (model.decimal && model.decimal.$invalid) {
                errors.push(model.decimal.$message);
            }
        }
        return errors;
    }
};

const convertToBytes = (unit: "kB" | "MB" | "GB", limit: number): number => {
    /**
     * Takes a unit (kB, MB, GB) and a size limit as parameters. Based on the unit provided,
     * it multiplies the size limit by the appropriate factor to convert it to bytes. Then,
     * it returns the calculated size limit in bytes.
     * @param {"kB" | "MB" | "GB"} unit
     * @param {number} limit
     * @returns {number}
     */
    switch (unit) {
        case "kB":
            return limit * 1024;
        case "MB":
            return limit * 1024 * 1024;
        case "GB":
            return limit * 1024 * 1024 * 1024;
        default:
            return 0;
    }
};

export const validateFileSize = (
    files: any,
    unit: "kB" | "MB" | "GB",
    sizeLimit: number,
): { valid: boolean; message: string } => {
    /**
     * Validates whether the given file or array of files exceeds a specified size limit.
     * @param {any} files
     * @param {"kB" | "MB" | "GB"} unit
     * @param {number} sizeLimit
     * @returns {{ valid: boolean; message: string }}
     * @example
     * validateFileSize(fileInput.files, "MB", 2);
     */
    if (!files) {
        return { valid: false, message: "No file selected" };
    }

    const fileArray = Array.isArray(files) ? files : [files];

    const limit = convertToBytes(unit, sizeLimit);

    const fileNames: string[] = [];
    let valid = true;

    for (const file of fileArray) {
        if (file.size >= limit) {
            valid = false;
        }
        fileNames.push(file.name);
    }

    const message = valid
        ? ""
        : `The file${fileNames.length > 1 ? "s" : ""} ${fileNames.join(", ")} exceed${fileNames.length > 1 ? "" : "s"} the ${sizeLimit}${unit} individual file size`;

    return {
        valid,
        message,
    };
};

export const validateBacsReference = (
    reference: string,
): { valid: boolean; message: string } => {
    /**
     *     Validate BACS credit reference according to specific rules.
     *     Rules:
            1. Only including valid characters:
                A–Z (Alpha characters),
                0–9 (Numeric characters).
                "." (Full stop),
                "&" (Ampersand),
                "/" (Slash),
                "-" (Hyphen),
                " " (Blank space)
            2. Maximum length of 18 characters.
            3. Minimum of 6 alphanumeric characters (A-Z, 0-9)
            4. Cannot consist of all the same characters.
            5. Cannot contain the sequence "DDIC" in the first four characters.
     * @param {string} reference - The BACS reference string to validate.
     * @returns {{ valid: boolean; message: string }} - An object indicating whether the reference is valid and an associated message.
     */
    if (!reference) return { valid: true, message: "" }; // Optional field, no error if empty

    // Rule 1: Only valid characters
    if (!/^[A-Z0-9.&/\- ]*$/.test(reference)) {
        return {
            valid: false,
            message:
                "Reference contains invalid characters. Only A-Z, 0-9, ., &, /, -, and spaces are allowed",
        };
    }

    // Rule 2: Maximum length 18
    if (reference.length > 18) {
        return {
            valid: false,
            message: "Reference must not exceed 18 characters",
        };
    }

    // Rule 3: Minimum 6 alphanumeric characters
    const alphanumericCount = (reference.match(/[A-Z0-9]/g) || []).length;
    if (alphanumericCount < 6) {
        return {
            valid: false,
            message:
                "Reference must contain at least 6 alphanumeric characters",
        };
    }

    // Rule 4: Cannot consist of all the same characters
    const uniqueChars = new Set(reference);
    if (uniqueChars.size === 1) {
        return {
            valid: false,
            message: "Reference cannot consist of all the same characters",
        };
    }

    // Rule 5: Cannot start with "DDIC"
    if (reference.length >= 4 && reference.startsWith("DDIC")) {
        return {
            valid: false,
            message:
                "Reference cannot start with 'DDIC' (reserved for bank use)",
        };
    }

    return { valid: true, message: "" }; // Validation passed
};
